import {Form, Formik, FormikProps} from 'formik';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {settingsApi} from 'api';
import {
    AssignmentSelectors, Button, Card, DataWrapper, ExternalIntegrationPurposeSelect, InputFormik, PasswordInputFormik,
    PredefinedNotifierType, TextAreaFormik, ToggleFormik, UnsavedChangesLeavePrompt
} from 'components';
import {UserContext} from 'context';
import {AiProvider, ExternalIntegrationPurpose, ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {MastraSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.MASTRA;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type FormTemplateProps = {
    loading: boolean;
    maskedApiKey: string;
    // Memory only affects the conversational client-text-chat flow; aux purposes read a one-shot transcript.
    showMemory: boolean;
    // Same flow: only the client text chat sends the agent speech to transcribe.
    showLanguage: boolean;
} & FormikProps<MastraSettings>;

const FIELDS = {
    ENABLED: 'enabled',
    BASE_URL: 'baseUrl',
    AGENT_ID: 'agentId',
    API_KEY: 'authentication.apiKey',
    MEMORY: 'memory',
    LANGUAGE: 'language',
    BOT_INSTRUCTION: 'promts.botInstruction',
    KNOWLEDGE_BASE: 'promts.knowledgeBase',
};

const FormTemplate: React.FC<FormTemplateProps> = ({
    loading, values, initialValues, submitForm, isValid, maskedApiKey, showMemory, showLanguage
}) => {
    const {t} = useTranslation();
    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Card>
                    <div className='tw-max-w-104'>
                        <ToggleFormik
                            name={FIELDS.ENABLED}
                            label={t(T_KEYS.LABELS.ENABLED)}
                            className='tw-my-4'
                        />
                        <InputFormik
                            name={FIELDS.BASE_URL}
                            label={t(T_KEYS.LABELS.BASE_URL)}
                            className='tw-w-full'
                            required={values.enabled}
                            spacing='top'
                        />
                        <InputFormik
                            name={FIELDS.AGENT_ID}
                            label={t(T_KEYS.LABELS.AGENT_ID)}
                            className='tw-w-full'
                            required={values.enabled}
                            spacing='top'
                        />
                        <PasswordInputFormik
                            name={FIELDS.API_KEY}
                            label={t(T_KEYS.LABELS.API_KEY)}
                            className='tw-w-full tw-mt-4'
                            spacing='bottom'
                            required={values.enabled && !maskedApiKey}
                            description={maskedApiKey}
                            autoComplete='one-time-code'
                        />
                        {showMemory &&
                            <ToggleFormik
                                name={FIELDS.MEMORY}
                                label={t(T_KEYS.LABELS.MEMORY)}
                                className='tw-my-4'
                            />}
                        {showLanguage &&
                            <InputFormik
                                name={FIELDS.LANGUAGE}
                                label={t(T_KEYS.LABELS.LANGUAGE)}
                                className='tw-w-full'
                                spacing='top'
                            />}
                        <TextAreaFormik
                            name={FIELDS.BOT_INSTRUCTION}
                            label={t(T_KEYS.LABELS.BOT_INSTRUCTION)}
                            rows={6}
                            spacing='top'
                        />
                        <TextAreaFormik
                            name={FIELDS.KNOWLEDGE_BASE}
                            label={t(T_KEYS.LABELS.KNOWLEDGE_BASE)}
                            rows={6}
                            spacing='top'
                        />

                        <div className='tw-text-right tw-mt-controls'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges || !isValid}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </div>
                </Card>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

type MastraSettingsFormProps = {
    groupId: string;
    integrationPurpose: ExternalIntegrationPurpose;
};

const MastraSettingsForm: React.FC<MastraSettingsFormProps> = ({groupId, integrationPurpose}) => {
    const {t} = useTranslation();
    const [maskedApiKey, setMaskedApiKey] = useState('');

    const [initialValues, setInitialValues] = useState<MastraSettings>({
        enabled: false,
        baseUrl: '',
        agentId: '',
        memory: false,
        language: '',
        authentication: {
            apiKey: ''
        },
        promts: {
            botInstruction: '',
            knowledgeBase: ''
        }
    });

    const {
        send: fetchSettingsFn, loading: fetchLoading, data: settings
    } = useApiData(settingsApi.getMastra, {notificationType: PredefinedNotifierType.FetchError});

    const fetchSettings = useCallback(() => {
        fetchSettingsFn(integrationPurpose, groupId);
    }, [integrationPurpose, groupId, fetchSettingsFn]);

    const {send: updateSettings, loading: updateLoading} = useApiData(settingsApi.updateMastra,
        {onSuccess: fetchSettings, notificationType: PredefinedNotifierType.Save});

    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.ENABLED]: Yup.boolean(),
        [FIELDS.BASE_URL]: Yup.string()
            .when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            }),
        [FIELDS.AGENT_ID]: Yup.string()
            .when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            }),
        authentication: Yup.object().shape({
            apiKey: Yup.string()
                .when(FIELDS.ENABLED, {
                    is: true,
                    then: (schema) => maskedApiKey ? schema :
                        schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
                })
        })
    }), [t, maskedApiKey]);

    const onSubmit = useCallback((values: MastraSettings) => {
        updateSettings(integrationPurpose, values, groupId);
    }, [integrationPurpose, groupId, updateSettings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    useEffect(() => {
        setMaskedApiKey(settings?.authentication?.apiKey);
        setInitialValues({
            enabled: !!settings?.enabled,
            baseUrl: settings?.baseUrl || '',
            agentId: settings?.agentId || '',
            memory: !!settings?.memory,
            language: settings?.language || '',
            authentication: {
                apiKey: ''
            },
            promts: {
                botInstruction: settings?.promts?.botInstruction || '',
                knowledgeBase: settings?.promts?.knowledgeBase || ''
            }
        });
    }, [settings]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={onSubmit}
        >
            {(props) => (
                <FormTemplate
                    {...props}
                    maskedApiKey={maskedApiKey}
                    showMemory={integrationPurpose === ExternalIntegrationPurpose.ClientTextChat}
                    showLanguage={integrationPurpose === ExternalIntegrationPurpose.ClientTextChat}
                    loading={fetchLoading || updateLoading}
                />
            )}
        </Formik>
    );
};

// TODO: move out from sip settings
export const MastraSettingsPage: React.FC = () => {
    const {settings: {providerPurposes}} = useContext(UserContext);
    const purposes = useMemo(() => providerPurposes[AiProvider.Mastra] || [], [providerPurposes]);
    const [selectedGroupId, setSelectedGroupId] = useState<string>(null);
    const [integrationPurpose, setIntegrationPurpose] = useState<ExternalIntegrationPurpose>(ExternalIntegrationPurpose.ClientTextChat);
    const formKey = useMemo(() => JSON.stringify([selectedGroupId, integrationPurpose]), [selectedGroupId, integrationPurpose]);

    return (
        <Fragment>
            <div className='tw-mb-4 tw-max-w-104'>
                <AssignmentSelectors
                    selectedGroupId={selectedGroupId}
                    onChange={setSelectedGroupId}
                >
                    {({renderGroup}) => renderGroup()}
                </AssignmentSelectors>
            </div>
            <ExternalIntegrationPurposeSelect
                value={integrationPurpose}
                purposes={purposes}
                className='tw-mb-4 tw-max-w-104'
                onChange={setIntegrationPurpose}
            />
            <MastraSettingsForm
                key={formKey}
                groupId={selectedGroupId}
                integrationPurpose={integrationPurpose}
            />
        </Fragment>
    );
};
